import { useEffect, useState } from 'react';
import { highlight, highlightPlain } from '../features/codedrop/highlighter';

/**
 * useHighlight — React hook wrapper around the async highlighter.
 *
 * Returns plain (un-tokenised) line spans immediately so the caller can
 * paint *something* without waiting for the grammar chunk. Once the grammar
 * has loaded and tokenisation finishes, the returned array is replaced with
 * the syntax-highlighted version and the consumer re-renders.
 *
 * The effect captures a `cancelled` flag in closure so a fast-following
 * re-run (different `text` / `lang`) doesn't race the previous promise and
 * overwrite the newer output with stale tokens.
 */
export function useHighlight(text, lang) {
  const [lines, setLines] = useState(() => highlightPlain(text));

  useEffect(() => {
    let cancelled = false;
    // Reset to plain immediately on input change so we don't paint stale
    // tokens for the previous text while the new grammar resolves.
    setLines(highlightPlain(text));
    highlight(text, lang).then((out) => {
      if (!cancelled) setLines(out);
    });
    return () => {
      cancelled = true;
    };
  }, [text, lang]);

  return lines;
}

export default useHighlight;
